// FinKloz — Person Detail Panel (Stock Options per Person)
// Slide-out panel showing all certificates for one person,
// combined vesting chart, vesting pause, and exercise/forfeit actions.

import React, { useState, useMemo } from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, amber, amberLight, useTheme } from '../../tokens';
import { percentVested, vestedOptions, vestingSchedule } from '../../services/vestingEngine';

const teal = '#0D9488';

function fmt(n) {
  if (!n || n === 0) return '--';
  return Math.round(n).toLocaleString('en-CA');
}

// Distinct colors for overlaid vesting curves
const CURVE_COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#14B8A6'];

export default function PersonDetailPanel({
  personName, grants, movements, asOfDate, onClose,
  onPauseVesting, onResumeVesting, onAddMovement, onSelectGrant,
}) {
  const t = useTheme();
  const [showExercise, setShowExercise] = useState(false);
  const [showForfeit, setShowForfeit] = useState(false);
  const [exerciseGrantId, setExerciseGrantId] = useState(null);
  const [exerciseQty, setExerciseQty] = useState(0);
  const [exerciseDate, setExerciseDate] = useState(new Date().toISOString().split('T')[0]);
  const [forfeitGrantId, setForfeitGrantId] = useState(null);
  const [forfeitQty, setForfeitQty] = useState(0);
  const [forfeitDate, setForfeitDate] = useState(new Date().toISOString().split('T')[0]);
  const [showPauseForm, setShowPauseForm] = useState(false);
  const [pauseStart, setPauseStart] = useState('');
  const [pauseEnd, setPauseEnd] = useState('');
  const [pauseReason, setPauseReason] = useState('');

  // Combined vesting chart — shared absolute timeline across all certificates
  const chartData = useMemo(() => {
    if (grants.length === 0) return { curves: [], globalStart: null, globalEnd: null };

    // Compute global time range: earliest vest start → latest vest end (vest start + 4 years)
    let globalStartMs = Infinity, globalEndMs = -Infinity;
    grants.forEach(g => {
      const vs = new Date(g.vestStart).getTime();
      const ve = new Date(g.vestStart);
      ve.setFullYear(ve.getFullYear() + 4);
      if (vs < globalStartMs) globalStartMs = vs;
      if (ve.getTime() > globalEndMs) globalEndMs = ve.getTime();
    });
    const globalRange = globalEndMs - globalStartMs;

    const curves = grants.map((g, idx) => {
      const schedule = vestingSchedule(g.options, g.vestStart, g.expiry);
      const vestStartMs = new Date(g.vestStart).getTime();
      const color = CURVE_COLORS[idx % CURVE_COLORS.length];

      // Map absolute dates to global x [0, 1]
      const toX = (ms) => (ms - globalStartMs) / globalRange;

      const cliffMs = vestStartMs + 365 * 86400000;
      const points = [
        { x: toX(vestStartMs), y: 0 },            // grant starts at 0%
        { x: toX(cliffMs) - 0.001, y: 0 },        // just before cliff: still 0%
      ];

      schedule.forEach(s => {
        const dateMs = new Date(s.date).getTime();
        points.push({ x: Math.min(toX(dateMs), 1), y: s.vestedPct });
      });

      // Extend to the certificate's own vest end
      const vestEndMs = vestStartMs + 4 * 365.25 * 86400000;
      points.push({ x: Math.min(toX(vestEndMs), 1), y: points[points.length - 1]?.y || 0 });

      return { grant: g, points, color, schedule };
    });

    return {
      curves,
      globalStart: new Date(globalStartMs),
      globalEnd: new Date(globalEndMs),
    };
  }, [grants]);

  if (!grants || grants.length === 0) return null;

  const rel = grants[0].rel;
  const hasPause = grants.some(g => g.vestingPaused);

  // Summary stats
  const totalGranted = grants.reduce((s, g) => s + g.options, 0);
  const totalVested = grants.reduce((s, g) => s + vestedOptions(g.options, g.vestStart, asOfDate), 0);
  const totalUnvested = totalGranted - totalVested;

  // Movements for this person's grants
  const grantIds = new Set(grants.map(g => g.id));
  const personMovements = (movements || []).filter(m => grantIds.has(m.grantId));
  const totalExercised = personMovements.filter(m => m.type === 'exercised').reduce((s, m) => s + m.options, 0);
  const totalForfeited = personMovements.filter(m => m.type === 'forfeited').reduce((s, m) => s + m.options, 0);

  const handleExercise = () => {
    if (!exerciseGrantId || !exerciseQty) return;
    onAddMovement(exerciseGrantId, 'exercised', exerciseDate, exerciseQty, null);
    setShowExercise(false);
    setExerciseQty(0);
  };

  const handleForfeit = () => {
    if (!forfeitGrantId || !forfeitQty) return;
    onAddMovement(forfeitGrantId, 'forfeited', forfeitDate, forfeitQty, null);
    setShowForfeit(false);
    setForfeitQty(0);
  };

  const handlePause = () => {
    if (!pauseStart) return;
    grants.forEach(g => {
      if (!g.vestingPaused) onPauseVesting(g.id, pauseStart, pauseEnd || null, pauseReason);
    });
    setShowPauseForm(false);
    setPauseStart('');
    setPauseEnd('');
    setPauseReason('');
  };

  const handleResume = () => {
    grants.forEach(g => {
      if (g.vestingPaused) onResumeVesting(g.id);
    });
  };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
      background: t.bgCard || '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
      zIndex: 200, display: 'flex', flexDirection: 'column', fontFamily: sans,
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary }}>{personName}</div>
          <div style={{ fontSize: 11, color: t.textSecondary, marginTop: 2 }}>{rel} | {grants.length} certificate{grants.length > 1 ? 's' : ''}</div>
        </div>
        <button onClick={onClose} style={{
          border: 'none', background: t.inputBg, borderRadius: 6, width: 28, height: 28,
          cursor: 'pointer', fontSize: 14, color: t.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>&times;</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {/* Summary strip */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 16,
          padding: '10px 8px', background: greenLight, borderRadius: 6,
        }}>
          {[
            { label: 'Granted', value: totalGranted, color: t.textPrimary },
            { label: 'Vested', value: totalVested, color: green },
            { label: 'Unvested', value: totalUnvested, color: amber },
            { label: 'Exercised', value: totalExercised, color: teal },
            { label: 'Forfeited', value: totalForfeited, color: red },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, color: s.color }}>{fmt(s.value)}</div>
              <div style={{ fontFamily: sans, fontSize: 8, color: greenDark }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Combined Vesting Chart */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>Vesting Schedule (all certificates)</div>
          <div style={{ height: 140, position: 'relative', background: t.bgSurface, borderRadius: 6, border: `1px solid ${t.borderLight}`, padding: '8px 4px 22px 30px' }}>
            <svg width="100%" height="100%" viewBox="0 0 380 100" preserveAspectRatio="none">
              {/* Grid lines */}
              {[0, 25, 50, 75, 100].map(pct => (
                <line key={pct} x1="0" y1={100 - pct} x2="380" y2={100 - pct} stroke={t.borderLight} strokeWidth="0.5" />
              ))}
              {/* Vesting curve per certificate */}
              {chartData.curves.map(({ points, color }, idx) => (
                <path key={idx}
                  d={'M' + points.map(p => `${p.x * 380},${100 - p.y * 100}`).join(' L')}
                  fill="none" stroke={color} strokeWidth="2" opacity={0.8}
                />
              ))}
              {/* Today marker */}
              {chartData.globalStart && (() => {
                const globalRange = chartData.globalEnd.getTime() - chartData.globalStart.getTime();
                const todayMs = new Date(asOfDate).getTime() - chartData.globalStart.getTime();
                const x = Math.max(0, Math.min(todayMs / globalRange * 380, 380));
                const labelX = x > 330 ? x - 3 : x + 3;
                const anchor = x > 330 ? 'end' : 'start';
                return (
                  <g>
                    <line x1={x} y1="0" x2={x} y2="100" stroke={teal} strokeWidth="1.5" strokeDasharray="3,2" />
                    <text x={labelX} y="8" fontFamily={sans} fontSize="7" fill={teal} fontWeight="600" textAnchor={anchor}>Today</text>
                  </g>
                );
              })()}
            </svg>
            <div style={{ position: 'absolute', left: 2, top: 6, fontFamily: mono, fontSize: 7, color: t.textMuted }}>100%</div>
            <div style={{ position: 'absolute', left: 2, bottom: 20, fontFamily: mono, fontSize: 7, color: t.textMuted }}>0%</div>
            {chartData.globalStart && (
              <div style={{ position: 'absolute', left: 30, bottom: 2, fontFamily: mono, fontSize: 7, color: t.textMuted }}>
                {chartData.globalStart.toISOString().split('T')[0]}
              </div>
            )}
            {chartData.globalEnd && (
              <div style={{ position: 'absolute', right: 4, bottom: 2, fontFamily: mono, fontSize: 7, color: t.textMuted }}>
                {chartData.globalEnd.toISOString().split('T')[0]}
              </div>
            )}
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            {chartData.curves.map(({ grant: g, color }, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, fontFamily: sans, color: t.textSecondary }}>
                <span style={{ width: 10, height: 3, background: color, borderRadius: 1, display: 'inline-block' }} />
                {g.grantDate} ({fmt(g.options)})
              </div>
            ))}
          </div>
        </div>

        {/* Certificate table */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>Certificates</div>
          <div style={{
            display: 'grid', gridTemplateColumns: '80px 60px 60px 50px 70px 70px',
            gap: 2, padding: '6px 6px', background: green, borderRadius: '6px 6px 0 0',
            fontFamily: sans, fontSize: 8, fontWeight: 600, color: '#fff',
          }}>
            <span>Grant Date</span>
            <span style={{ textAlign: 'right' }}># Options</span>
            <span style={{ textAlign: 'right' }}>Price</span>
            <span>CCY</span>
            <span>Cohort</span>
            <span style={{ textAlign: 'right' }}>Vested</span>
          </div>
          {grants.map((g, i) => {
            const v = vestedOptions(g.options, g.vestStart, asOfDate);
            const pct = percentVested(g.vestStart, asOfDate);
            return (
              <div key={g.id} onClick={() => onSelectGrant && onSelectGrant(g)} style={{
                display: 'grid', gridTemplateColumns: '80px 60px 60px 50px 70px 70px',
                gap: 2, padding: '5px 6px', background: i % 2 === 0 ? '#fff' : '#F5F5F7',
                borderBottom: `1px solid ${t.borderLight}`, fontSize: 10,
                cursor: onSelectGrant ? 'pointer' : 'default',
              }}>
                <span style={{ fontFamily: mono, color: t.textPrimary }}>{g.grantDate}</span>
                <span style={{ fontFamily: mono, color: t.textPrimary, textAlign: 'right', fontWeight: 600 }}>{fmt(g.options)}</span>
                <span style={{ fontFamily: mono, color: t.textSecondary, textAlign: 'right' }}>${g.price}</span>
                <span style={{ fontFamily: mono, color: t.textMuted }}>{g.currency}</span>
                <span style={{ fontFamily: sans, color: green, fontSize: 9 }}>{g.cohort}</span>
                <span style={{ fontFamily: mono, color: pct >= 1 ? green : amber, textAlign: 'right' }}>{fmt(v)} ({Math.round(pct * 100)}%)</span>
              </div>
            );
          })}
        </div>

        {/* Movements history */}
        {personMovements.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>Movement History</div>
            {personMovements.map((m, i) => (
              <div key={m.id || i} style={{
                display: 'flex', justifyContent: 'space-between', padding: '4px 0',
                borderBottom: `1px solid ${t.borderLight}`, fontSize: 10, fontFamily: mono,
              }}>
                <span style={{ color: m.type === 'exercised' ? teal : red, fontWeight: 600 }}>
                  {m.type === 'exercised' ? 'Exercised' : 'Forfeited'}
                </span>
                <span style={{ color: t.textSecondary }}>{m.date}</span>
                <span style={{ color: t.textPrimary }}>{fmt(m.options)} options</span>
              </div>
            ))}
          </div>
        )}

        {/* Vesting Pause */}
        <div style={{ marginBottom: 16, padding: '12px', background: amberLight, borderRadius: 6, border: `1px solid ${amber}` }}>
          <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: amber, marginBottom: 4 }}>
            Vesting Pause (30-Day Rule)
          </div>
          <div style={{ fontFamily: sans, fontSize: 10, color: t.textSecondary, marginBottom: 8 }}>
            Pauses vesting on all certificates for this person. Per the stock option plan, vesting can be paused if the employee is not working for 30+ consecutive days.
          </div>
          {hasPause ? (
            <div>
              <div style={{ fontFamily: mono, fontSize: 11, color: red }}>
                Paused: {grants.find(g => g.vestingPaused)?.vestingPauseStart} to {grants.find(g => g.vestingPaused)?.vestingPauseEnd || 'present'}
              </div>
              <div style={{ fontFamily: sans, fontSize: 10, color: t.textSecondary, marginTop: 2 }}>
                {grants.find(g => g.vestingPaused)?.vestingPauseReason}
              </div>
              <button onClick={handleResume} style={{
                fontFamily: sans, fontSize: 10, marginTop: 6, padding: '3px 8px',
                border: `1px solid ${green}`, borderRadius: 3, background: greenLight, color: greenDark, cursor: 'pointer',
              }}>
                Resume Vesting
              </button>
            </div>
          ) : showPauseForm ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 2 }}>Pause Start</div>
                  <input type="date" value={pauseStart} onChange={e => setPauseStart(e.target.value)}
                    style={{ fontFamily: mono, fontSize: 11, width: '100%', padding: '4px 6px', border: `1px solid ${t.border}`, borderRadius: 4 }} />
                </div>
                <div>
                  <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 2 }}>Pause End (leave empty if ongoing)</div>
                  <input type="date" value={pauseEnd} onChange={e => setPauseEnd(e.target.value)}
                    style={{ fontFamily: mono, fontSize: 11, width: '100%', padding: '4px 6px', border: `1px solid ${t.border}`, borderRadius: 4 }} />
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 2 }}>Reason</div>
                <input type="text" value={pauseReason} onChange={e => setPauseReason(e.target.value)}
                  placeholder="e.g., Medical leave, unpaid leave..."
                  style={{ fontFamily: sans, fontSize: 11, width: '100%', padding: '4px 6px', border: `1px solid ${t.border}`, borderRadius: 4 }} />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setShowPauseForm(false)}
                  style={{ fontFamily: sans, fontSize: 10, padding: '3px 8px', border: `1px solid ${t.border}`, borderRadius: 3, background: t.bg, color: t.textSecondary, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handlePause} disabled={!pauseStart}
                  style={{ fontFamily: sans, fontSize: 10, fontWeight: 600, padding: '3px 10px', border: `1px solid ${pauseStart ? amber : t.border}`, borderRadius: 3, background: pauseStart ? amber : t.bgSurface, color: pauseStart ? '#fff' : t.textMuted, cursor: pauseStart ? 'pointer' : 'not-allowed' }}>
                  Confirm Pause
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowPauseForm(true)} style={{
              fontFamily: sans, fontSize: 11, padding: '5px 10px',
              border: `1px solid ${amber}`, borderRadius: 4, background: '#fff', color: amber, cursor: 'pointer',
            }}>
              Pause Vesting
            </button>
          )}
        </div>

        {/* Exercise / Forfeit Actions */}
        <div style={{ padding: '12px', background: t.bgSurface, borderRadius: 6, border: `1px solid ${t.border}` }}>
          <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>
            Actions
          </div>
          <div style={{ fontFamily: sans, fontSize: 10, color: t.textSecondary, marginBottom: 8 }}>
            Record an exercise or forfeiture for a specific certificate.
          </div>

          {/* Exercise form */}
          {showExercise ? (
            <div style={{ padding: 10, background: greenLight, borderRadius: 6, marginBottom: 8 }}>
              <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: greenDark, marginBottom: 8 }}>Exercise Options</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 9, color: t.textMuted, marginBottom: 2 }}>Certificate</div>
                  <select value={exerciseGrantId || ''} onChange={e => setExerciseGrantId(e.target.value)}
                    style={{ fontFamily: mono, fontSize: 11, width: '100%', padding: '4px', border: `1px solid ${t.border}`, borderRadius: 4 }}>
                    <option value="">Select...</option>
                    {grants.map(g => (
                      <option key={g.id} value={g.id}>{g.grantDate} ({fmt(g.options)} @ ${g.price})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: t.textMuted, marginBottom: 2 }}>Quantity</div>
                  <input type="number" value={exerciseQty || ''} onChange={e => setExerciseQty(parseInt(e.target.value) || 0)}
                    style={{ fontFamily: mono, fontSize: 11, width: '100%', padding: '4px', border: `1px solid ${t.border}`, borderRadius: 4 }} />
                </div>
                <div>
                  <div style={{ fontSize: 9, color: t.textMuted, marginBottom: 2 }}>Date</div>
                  <input type="date" value={exerciseDate} onChange={e => setExerciseDate(e.target.value)}
                    style={{ fontFamily: mono, fontSize: 11, width: '100%', padding: '4px', border: `1px solid ${t.border}`, borderRadius: 4 }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setShowExercise(false)}
                  style={{ fontSize: 10, padding: '3px 8px', border: `1px solid ${t.border}`, borderRadius: 3, background: '#fff', color: t.textSecondary, cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleExercise} disabled={!exerciseGrantId || !exerciseQty}
                  style={{ fontSize: 10, fontWeight: 600, padding: '3px 10px', border: `1px solid ${green}`, borderRadius: 3, background: green, color: '#fff', cursor: exerciseGrantId && exerciseQty ? 'pointer' : 'not-allowed' }}>Confirm Exercise</button>
              </div>
            </div>
          ) : showForfeit ? (
            <div style={{ padding: 10, background: redLight, borderRadius: 6, marginBottom: 8 }}>
              <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: red, marginBottom: 8 }}>Forfeit Options</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 9, color: t.textMuted, marginBottom: 2 }}>Certificate</div>
                  <select value={forfeitGrantId || ''} onChange={e => setForfeitGrantId(e.target.value)}
                    style={{ fontFamily: mono, fontSize: 11, width: '100%', padding: '4px', border: `1px solid ${t.border}`, borderRadius: 4 }}>
                    <option value="">Select...</option>
                    {grants.map(g => (
                      <option key={g.id} value={g.id}>{g.grantDate} ({fmt(g.options)} @ ${g.price})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: t.textMuted, marginBottom: 2 }}>Quantity</div>
                  <input type="number" value={forfeitQty || ''} onChange={e => setForfeitQty(parseInt(e.target.value) || 0)}
                    style={{ fontFamily: mono, fontSize: 11, width: '100%', padding: '4px', border: `1px solid ${t.border}`, borderRadius: 4 }} />
                </div>
                <div>
                  <div style={{ fontSize: 9, color: t.textMuted, marginBottom: 2 }}>Date</div>
                  <input type="date" value={forfeitDate} onChange={e => setForfeitDate(e.target.value)}
                    style={{ fontFamily: mono, fontSize: 11, width: '100%', padding: '4px', border: `1px solid ${t.border}`, borderRadius: 4 }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setShowForfeit(false)}
                  style={{ fontSize: 10, padding: '3px 8px', border: `1px solid ${t.border}`, borderRadius: 3, background: '#fff', color: t.textSecondary, cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleForfeit} disabled={!forfeitGrantId || !forfeitQty}
                  style={{ fontSize: 10, fontWeight: 600, padding: '3px 10px', border: `1px solid ${red}`, borderRadius: 3, background: red, color: '#fff', cursor: forfeitGrantId && forfeitQty ? 'pointer' : 'not-allowed' }}>Confirm Forfeit</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowExercise(true)} style={{
                fontFamily: sans, fontSize: 11, fontWeight: 600, padding: '6px 12px', borderRadius: 4,
                border: `1px solid ${green}`, background: greenLight, color: greenDark, cursor: 'pointer', flex: 1,
              }}>
                Exercise
              </button>
              <button onClick={() => setShowForfeit(true)} style={{
                fontFamily: sans, fontSize: 11, fontWeight: 600, padding: '6px 12px', borderRadius: 4,
                border: `1px solid ${red}`, background: redLight, color: red, cursor: 'pointer', flex: 1,
              }}>
                Forfeit
              </button>
            </div>
          )}
        </div>

        {/* Termination / Departure */}
        <div style={{ marginTop: 16, padding: '12px', background: '#FEF2F2', borderRadius: 6, border: `1px solid ${red}` }}>
          <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: red, marginBottom: 4 }}>
            Termination / Departure
          </div>
          <div style={{ fontFamily: sans, fontSize: 10, color: t.textSecondary, marginBottom: 8 }}>
            When an employee departs, unvested options are forfeited automatically.
            Vested options must be exercised within the post-termination exercise period (typically 90 days).
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8, fontFamily: mono, fontSize: 10 }}>
            <div style={{ padding: '6px 8px', background: '#fff', borderRadius: 4 }}>
              <div style={{ fontFamily: sans, fontSize: 8, color: t.textMuted, marginBottom: 2 }}>Vested (exercisable)</div>
              <div style={{ color: green, fontWeight: 600 }}>{fmt(totalVested - totalExercised)}</div>
            </div>
            <div style={{ padding: '6px 8px', background: '#fff', borderRadius: 4 }}>
              <div style={{ fontFamily: sans, fontSize: 8, color: t.textMuted, marginBottom: 2 }}>Unvested (to forfeit)</div>
              <div style={{ color: red, fontWeight: 600 }}>{fmt(totalUnvested)}</div>
            </div>
          </div>
          <div style={{ fontFamily: sans, fontSize: 9, color: t.textMuted, padding: '6px 8px', background: '#fff', borderRadius: 4 }}>
            Post-termination exercise window: <strong>90 days</strong> from departure date.
            After this period, unexercised vested options are forfeited and return to the pool.
          </div>
        </div>
      </div>
    </div>
  );
}
